import clsx from "clsx";
import LogoAnimatedHappy from "components/svg/LogoAnimatedHappy";
import LogoAnimatedSurprised from "components/svg/LogoAnimatedSurprised";
import { Tab } from "bootstrap";
import { ReplyDisplay } from "components/game/ReplyDisplay";
import { ReportPhaseTwo } from "components/game/ReportCard";
import { Fragment, useEffect, useRef } from "react";
import { GameStatePlayerStateProps } from "types/GameStateProps";

// props
const showFavouritePlayerThreshold = 1.5;
const showFavouriteReplyThreshold = 2.5;
const thresholdShowPhase2 = 5;

export const GameStateReporting = (props: GameStatePlayerStateProps) => {
    const { gameState, playerState } = props;

    const gameScore = gameState.reporting?.llmScore;
    const favouriteReply = gameState.reporting?.favouriteReply;
    const playerScore = playerState?.reporting?.llmScore ?? gameState.reporting?.llmScore;
    const dimensions = playerState?.reporting?.dimensions ?? gameState.reporting?.dimensions ?? [];

    const tabPhase1 = useRef<HTMLButtonElement>(null);
    const tabPhase2 = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (gameState.animationStage !== undefined) {
            if (gameState.animationStage < thresholdShowPhase2 && tabPhase1.current && playerScore) {
                Tab.getOrCreateInstance(tabPhase1.current).show();
            } else if (tabPhase2.current) {
                Tab.getOrCreateInstance(tabPhase2.current).show();
            }
        }
    }, [gameState.animationStage, playerScore]);

    if (!gameScore || !playerScore || gameState.animationStage === undefined) {
        return null;
    }

    const { animationStage } = gameState;

    const roundedScore = gameScore > 3.5 ? 4 : gameScore > 2.5 ? 3 : gameScore > 1.5 ? 2 : 1;

    const initialSubMessage =
        roundedScore === 4
            ? "He absolutely LOVED what he read!!"
            : roundedScore === 3
            ? "He liked what everyone wrote and was very impressed!"
            : roundedScore === 2
            ? "He is proud of everybody for completing the game"
            : "He doesn't understand what is happening in this game";

    const secondaryTitle =
        roundedScore >= showFavouritePlayerThreshold
            ? "Franky had a favourite player today"
            : roundedScore > showFavouritePlayerThreshold
            ? "Franky was unable to pick a favourite reply :-/"
            : "Franky is confused!";

    const secondaryDescription =
        roundedScore >= showFavouritePlayerThreshold ? (
            <Fragment>
                He really liked <strong>{favouriteReply?.playerName}</strong>'s writing!
            </Fragment>
        ) : (
            "He hurt himself in his confusion!"
        );

    return (
        <Fragment>
            <div className="nav nav-tabs d-none" id="nav-report">
                <button
                    ref={tabPhase1}
                    type="button"
                    className="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#tab-phase1"
                >
                    Phase 1
                </button>

                <button
                    ref={tabPhase2}
                    type="button"
                    className="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#tab-phase2"
                >
                    Phase 2
                </button>
            </div>

            <div className="tab-content">
                <div className="tab-pane fade" id="tab-phase1">
                    <div className="vstack gap-3 text-center text-lg-start">
                        <div className="vstack gap-3">
                            <div className="vstack">
                                <div className="h2">Franky has been reading everyone's writing</div>

                                <div className={clsx("animate", animationStage < 1 ? "opacity-0" : "opacity-100")}>
                                    {initialSubMessage}
                                </div>
                            </div>
                        </div>

                        <div className="grid gap-4 gap-lg-5">
                            <div className="g-col-12 g-col-lg-4">
                                <div>
                                    {roundedScore === 1 && <LogoAnimatedSurprised stop={animationStage < 3} />}
                                    {roundedScore === 2 && <LogoAnimatedHappy stop={animationStage < 3} />}
                                    {roundedScore === 3 && <LogoAnimatedHappy stop={animationStage < 3} />}
                                    {roundedScore === 4 && <LogoAnimatedHappy stop={animationStage < 3} />}
                                </div>
                            </div>

                            <div className="g-col-12 g-col-lg-8 align-self-center">
                                <div className="vstack">
                                    <div
                                        className={clsx(
                                            "h4 fw-bold animate",
                                            animationStage >= 2 ? "opacity-100" : "opacity-0",
                                        )}
                                    >
                                        {secondaryTitle}
                                    </div>

                                    <div className={clsx("animate", animationStage >= 3 ? "opacity-100" : "opacity-0")}>
                                        {secondaryDescription}
                                    </div>
                                </div>
                            </div>

                            <div
                                className={clsx(
                                    "g-col-12 g-col-lg-8 animate",
                                    animationStage >= 4 ? "opacity-100" : "opacity-0",
                                )}
                            >
                                {gameScore < showFavouritePlayerThreshold ? (
                                    <div className="h4">Franky was too confused to pick a favourite player :-(</div>
                                ) : gameScore < showFavouriteReplyThreshold ? (
                                    <div className="h4">Franky was unable to pick a favourite reply :-/</div>
                                ) : (
                                    <div className="vstack">
                                        <div className="h4">Here's one of his favourite replies</div>

                                        <div>
                                            Congratulations <strong>{favouriteReply?.playerName}!</strong>
                                        </div>

                                        {favouriteReply && (
                                            <div className="vstack gap-2">
                                                <ReplyDisplay
                                                    body={favouriteReply.body}
                                                    votes={favouriteReply.voteCount}
                                                    characterId={favouriteReply.characterId}
                                                    author={favouriteReply.playerName}
                                                    hasSticker={favouriteReply.hasSticker}
                                                    isWinner={favouriteReply.isWinner}
                                                    isPerfect={favouriteReply.isPerfect}
                                                />

                                                {favouriteReply.reactionCount > 0 && (
                                                    <div className="hstack gap-2 justify-content-end w-100">
                                                        {favouriteReply.reactionCount} ❤️
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tab-pane fade" id="tab-phase2">
                    <ReportPhaseTwo
                        code={gameState.code}
                        playerName={playerState?.playerName ?? "Whole class"}
                        dimensions={dimensions}
                        llmScore={playerScore}
                        animationStage={animationStage}
                        roundEnd={gameState.roundEnd}
                    />
                </div>
            </div>
        </Fragment>
    );
};
